import _get from 'lodash/get';
import _has from 'lodash/has';
import _isFunction from 'lodash/isFunction';
import { evaluateStep } from './utils/evaluateStep';
import { getCurrentStep } from './utils/getCurrentStep';
import { evaluateCondition } from './utils/evaluateCondition';

/**
 * Find the last failed step
 *
 * @param {Object} step The current step from merchant config
 * @param {Object} flowConfig Merchant config
 * @param {Object} props
 *
 * @returns {Object} last not finished step
 */
export const evaluateMerchantsData = (stepToStartChecking, flowConfig, props) => {
  if (!flowConfig) return null;
  return getLastFailedStep(stepToStartChecking, flowConfig, props);
};

/**
 * Find the last failed step
 *
 * @param {Object} step The current step from merchant config
 * @param {Object} flowConfig Merchant config
 * @param {Object} props
 *
 * @returns {Object} last not finished step
 */
export const getLastFailedStep = (step, flowConfig, props) => {
  const { merchant } = props;
  var lastFailedStep = step;
  var previousStepPath = evaluateStep('back', step, props);
  if (!previousStepPath) {
    return lastFailedStep;
  }
  var previousStep = getCurrentStep(flowConfig, props, { pathOverride: previousStepPath });
  while (previousStep && previousStep.id) {
    const isDataOK = checkStepData(previousStep, merchant, props);
    if (!isDataOK) {
      lastFailedStep = previousStep;
    }
    previousStepPath = evaluateStep('back', previousStep, props);
    if (!previousStepPath) {
      return lastFailedStep;
    }
    previousStep = getCurrentStep(flowConfig, props, { pathOverride: previousStepPath });
  }

  return lastFailedStep;
};

/**
 * Check if the step was finished
 *
 * @param {Object} step Step to check
 * @param {Object} merchant Merchant object
 * @param {Object} props properties object that was passed to the flow config
 *
 * @returns {Boolean} True if all data from config field "fieldsToFill" is presenting
 * in the merchant object, false if something is missing
 */
export const checkStepData = (step, merchant, props = {}) => {
  const fieldValidations = [];
  const fieldsToFill = step.Container.getFieldsToFill
    ? step.Container.getFieldsToFill({
        deploymentRegion: props.region,
        merchantCountry: props.country,
        countryConfig: props.countryConfig,
        regionConfig: props.regionConfig,
        onboardingType: '',
      })
    : _get(step, 'fieldsToFill', []);

  fieldsToFill.forEach(step => {
    const fieldNameFromStep = _get(step, 'fieldName');
    const roles = _get(step, 'roles', []);
    const isOptionalFromStep = _get(step, 'isOptional');
    const fieldIsExisting = Object.prototype.hasOwnProperty.call(merchant, fieldNameFromStep);

    // If a field has a condition for being optional, we evaluate that condition.
    const isOptional = isOptionalFromStep ? evaluateCondition(isOptionalFromStep, props) : false;

    // Fields are only validated if they are not optional.
    if (!isOptional) {
      if (fieldIsExisting && fieldNameFromStep === 'stakeholders') {
        const hasStakeholders = hasAnyStakeholderWithRoles(merchant, roles);
        fieldValidations.push(hasStakeholders);
      } else if (fieldIsExisting) {
        const isDataValid = validateMerchantData(step, merchant);
        fieldValidations.push(isDataValid);
      }
    }
  });

  return fieldValidations.every(isValid => isValid);
};

/**
 * Validates all the fields from "fieldsToFill" for the merchant object
 *
 * @param {Object} field Element from fieldsToFill object
 * @param {Object} merchant Merchant object
 *
 * @returns {Boolean} True for optional key, otherwise check for the value and
 * if the key exists on the merchant object
 */
export const validateMerchantData = (field, merchant) => {
  const fieldNameFromStep = _get(field, 'fieldName');
  const fieldsFromStep = _get(field, 'fields', {});
  const fieldsKeys = Object.keys(fieldsFromStep);
  const requiredWhenPartial = _get(field, 'requiredWhenPartial');

  return fieldsKeys.every(key => {
    const fieldsFromMerchantObject = _get(merchant, fieldNameFromStep);

    const value = _get(fieldsFromMerchantObject, key);
    const hasPartialRequiredFields = requiredWhenPartial
      ? hasPartialRequiredFieldsOnMerchantObject(fieldsFromMerchantObject, requiredWhenPartial, key)
      : false;

    // We need to emulate the state here if the optional config is a selector.
    // This will only work for the products-fe app for now
    const fakeState = { 'products-fe': { merchant: { data: merchant } } };

    // use [key, 'optional'] because the key could be a 'address.city', which is not a path
    // that exists on the config
    const fieldOptionalConfig = [_get(fieldsFromStep, [key, 'optional'], false)]
      .map(config => (_isFunction(config) ? config(fakeState) : config))
      .shift();

    const isFieldOptional = hasPartialRequiredFields ? false : fieldOptionalConfig;
    const isValueEmpty = value === null || value === undefined || value === '';

    return isFieldOptional || (_has(fieldsFromMerchantObject, key) && !isValueEmpty);
  });
};

/**
 * Check if the merchant has some stakeholders with any roles from fieldsToFill config
 *
 * @param {Object} merchant Merchant object
 * @param {Array} roles Roles to check in the merchant
 *
 * @returns {Boolean}
 */
export const hasAnyStakeholderWithRoles = (merchant, roles) => {
  const stakeholders = _get(merchant, 'stakeholders', [], roles);

  return (
    stakeholders &&
    stakeholders.some(stakeholder => stakeholder.roles.some(role => roles.includes(role)))
  );
};

/**
 * Check if merchant object has partially submitted optional fields
 *
 * @param {Object} merchant Merchant object
 * @param {Array} roles Roles to check in the merchant
 *
 * @returns {Boolean} True if the data with requiredWhenPartial key exists on the merchant object
 * and has at leat one field submitted
 */
export const hasPartialRequiredFieldsOnMerchantObject = (
  fieldsFromMerchantObject,
  requiredWhenPartial,
  currentPropKey,
) => {
  const regExp = new RegExp('^' + requiredWhenPartial + '[.](.*)');
  const merchantHasPartialInfo =
    Object.keys(_get(fieldsFromMerchantObject, requiredWhenPartial, {})).length > 0;
  return merchantHasPartialInfo && regExp.test(currentPropKey);
};
